import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { supabase } from '../lib/supabase';
import type { User, Session } from '@supabase/supabase-js';

interface AuthContextType {
  user: User | null;
  session: Session | null;
  loading: boolean;
  signIn: (email: string, password: string) => Promise<void>;
  signUp: (email: string, password: string, fullName: string) => Promise<void>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Verificar sessão existente
    supabase.auth.getSession().then(({ data: { session } }) => {
      console.log('🔐 Sessão recuperada:', session ? `Usuário: ${session.user.email}` : 'Sem sessão ativa');
      setSession(session);
      setUser(session?.user ?? null);
      setLoading(false);
    });

    // Escutar mudanças de autenticação
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      console.log('🔄 Estado de autenticação mudou:', _event, session ? `Usuário: ${session.user.email}` : 'Sem sessão');
      setSession(session);
      setUser(session?.user ?? null);
      setLoading(false);
    });

    return () => subscription.unsubscribe();
  }, []);

  const signIn = async (email: string, password: string) => {
    try {
      console.log('🔑 Iniciando login para:', email);
      
      // Validar formato do email
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(email)) {
        throw new Error('Formato de email inválido');
      }

      // Validar senha
      if (!password || password.length < 6) {
        throw new Error('A senha deve ter pelo menos 6 caracteres');
      }

      console.log('📧 Email válido, tentando autenticar...');
      
      const { data, error } = await supabase.auth.signInWithPassword({ 
        email: email.trim().toLowerCase(), 
        password 
      });
      
      if (error) {
        console.error('❌ Erro retornado pelo Supabase:', error.message);
        console.error('❌ Código do erro:', error.status);
        throw new Error(getErrorMessage(error.message));
      }
      
      if (!data.user) {
        console.error('❌ Login não retornou usuário');
        throw new Error('Erro ao fazer login. Tente novamente');
      }

      if (!data.session) {
        console.error('❌ Login não retornou sessão');
        throw new Error('Erro ao criar sessão. Tente novamente');
      }
      
      console.log('✅ Login bem-sucedido!');
      console.log('✅ Usuário:', data.user.email);
      console.log('✅ ID:', data.user.id);
      console.log('✅ Sessão criada:', data.session.access_token ? 'Token válido' : 'Sem token');
      
      // Verificar se o perfil existe
      const { data: profile, error: profileError } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', data.user.id)
        .single();
      
      if (profileError) {
        console.warn('⚠️ Perfil não encontrado, mas login foi bem-sucedido');
      } else {
        console.log('✅ Perfil encontrado:', profile.full_name);
      }
      
    } catch (error: any) {
      console.error('💥 Erro ao fazer login:', error.message);
      throw error;
    }
  };

  const signUp = async (email: string, password: string, fullName: string) => {
    try {
      console.log('📝 Iniciando cadastro para:', email);
      
      // Validar formato do email
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(email)) {
        throw new Error('Formato de email inválido');
      }

      // Validar senha
      if (!password || password.length < 6) {
        throw new Error('A senha deve ter pelo menos 6 caracteres');
      }

      // Validar nome
      if (!fullName || fullName.trim().length < 3) {
        throw new Error('O nome deve ter pelo menos 3 caracteres');
      }
      
      const { data, error } = await supabase.auth.signUp({ 
        email: email.trim().toLowerCase(), 
        password,
        options: {
          data: {
            full_name: fullName.trim()
          },
          emailRedirectTo: window.location.origin
        }
      });
      
      if (error) {
        console.error('❌ Erro ao criar conta:', error.message);
        throw new Error(getErrorMessage(error.message));
      }
      
      if (!data.user) {
        console.error('❌ Cadastro não retornou usuário');
        throw new Error('Erro ao criar conta. Tente novamente');
      }
      
      console.log('✅ Conta criada! ID:', data.user.id);
      console.log('📧 Email:', data.user.email);
      console.log('🔐 Confirmação necessária:', data.user.confirmed_at ? 'Não' : 'Sim');
      
      // Criar perfil do usuário
      console.log('📝 Criando perfil no banco de dados...');
      const { error: profileError } = await supabase.from('profiles').insert({
        id: data.user.id,
        email: email.trim().toLowerCase(),
        full_name: fullName.trim(),
        role: 'user'
      });
      
      if (profileError) {
        console.error('⚠️ Erro ao criar perfil:', profileError.message);
        // Não lançar erro aqui, pois a conta foi criada com sucesso
      } else {
        console.log('✅ Perfil criado com sucesso!');
      }
      
    } catch (error: any) {
      console.error('💥 Erro ao criar conta:', error.message);
      throw error;
    }
  };

  const signOut = async () => {
    try {
      console.log('👋 Fazendo logout...');
      const { error } = await supabase.auth.signOut();
      if (error) {
        console.error('❌ Erro ao fazer logout:', error.message);
        throw error;
      }
      console.log('✅ Logout realizado com sucesso!');
    } catch (error: any) {
      console.error('💥 Erro ao fazer logout:', error.message);
      throw error;
    }
  };

  return (
    <AuthContext.Provider value={{ user, session, loading, signIn, signUp, signOut }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth deve ser usado dentro de um AuthProvider');
  }
  return context;
}

// Função para traduzir mensagens de erro do Supabase
function getErrorMessage(error: string): string {
  const errorMessages: Record<string, string> = {
    'Invalid login credentials': 'Email ou senha incorretos. Verifique seus dados e tente novamente',
    'Email not confirmed': 'Email não confirmado. Verifique sua caixa de entrada',
    'User already registered': 'Este email já está cadastrado. Faça login ou use outro email',
    'Password should be at least 6 characters': 'A senha deve ter pelo menos 6 caracteres',
    'Unable to validate email address: invalid format': 'Formato de email inválido',
    'User not found': 'Usuário não encontrado. Verifique o email digitado',
    'Invalid email or password': 'Email ou senha incorretos',
    'Email rate limit exceeded': 'Muitas tentativas. Aguarde alguns minutos e tente novamente',
    'Signups not allowed for this instance': 'Cadastro não permitido neste momento',
    'Invalid credentials': 'Credenciais inválidas. Verifique seu email e senha',
    'Invalid email': 'Email inválido',
    'Signup requires a valid password': 'É necessário fornecer uma senha válida',
    'Database error saving new user': 'Erro ao salvar usuário no banco de dados',
    'User already exists': 'Este email já está cadastrado'
  };

  return errorMessages[error] || `Erro: ${error}`;
}
